import React, { useCallback, useMemo } from 'react'
import { formatNumberBR } from 'src/utils'
import { SalesReceivementBatch } from 'src/features/receivement/ReceivementTypes'
import { dropdownOptions } from './ActionDropdown'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from 'src/features/receivement/ReceivementState'
import * as S from '../styles'

interface Props {
  batch: SalesReceivementBatch;
}

export const SwapQuantityInput = React.memo(({ batch }: Props) => {
  const [quantityErrorMessage, setQuantityErrorMessage] = React.useState<string | undefined>()

  const dispatch = useDispatch()

  const batchState = useSelector(
    (state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')],
  )

  const validateSwapQuantity = useCallback((quantity) => {
    if (quantity < 0) {
      setQuantityErrorMessage('Valor inválido!')
      return false
    }

    if (quantity > batchState.acceptedQuantity) {
      setQuantityErrorMessage('Qtd. maior que a aceita!')
      return false
    }

    setQuantityErrorMessage(undefined)
    return true
  }, [batchState.acceptedQuantity])

  const handleQuantity = React.useCallback((event) => {
    let value = parseInt(event.target.value.replace(/[^0-9]/g, ''), 10)

    if (batch.measureUnit.isKg) {
      value /= 1000
    }

    validateSwapQuantity(value)

    if (Number.isNaN(value)) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            swapQuantity: undefined,
          },
        }),
      )
      return
    }

    dispatch(
      actions.updateBatchState({
        [batch.idsBatchItemOrder.join(',')]: {
          ...batchState,
          swapQuantity: value,
        },
      }),
    )
  }, [batch.idsBatchItemOrder, batch.measureUnit.isKg, batchState, dispatch, validateSwapQuantity])

  const formatInputNumber = useCallback(
    (swapQuantity) => {
      if (typeof swapQuantity !== 'number') {
        return ''
      }

      if (batch.measureUnit.isKg) {
        return formatNumberBR(swapQuantity, 3)
      }

      return formatNumberBR(swapQuantity)
    },
    [batch.measureUnit.isKg],
  )

  const swapQuantityFormatted = useMemo(
    () => formatInputNumber(batchState.swapQuantity || 0),
    [batchState.swapQuantity, formatInputNumber],
  )

  return (
    <S.QuantityWrapper>
      <S.Input
        aria-label='Quantidade a ser trocada'
        data-testid='swap-quantity-input'
        type='text'
        error={!!quantityErrorMessage}
        value={swapQuantityFormatted}
        onChange={handleQuantity}
        readOnly={
          (batchState.option && dropdownOptions[batchState.option] === dropdownOptions.REJECT_ALL)
          || (batchState.acceptedQuantity === 0 || batchState.acceptedQuantity === null)
        }
      />
      {quantityErrorMessage && <S.Error>{quantityErrorMessage}</S.Error>}
    </S.QuantityWrapper>
  )
})
